import {Component, inject, OnInit, signal} from '@angular/core';
import {DatePipe} from '@angular/common';
import {Router} from '@angular/router';
import {AuthenticationService} from '../../../_auth/authentication.service';
import {AppRoutes} from '../../../enums/routes';
import {ContactLinkHttpService} from '../../../_auth/contact/contact-link-http.service';
import {
  channelDisplayName,
  channelUnavailableMessage,
  ContactGate,
  ContactGateStatus,
  ContactLinkPurpose,
  ContactLinkPurposeValue
} from '../../../_auth/contact/contact-link.models';
import {ContactLinkWizardComponent} from './contact-link-wizard.component';

/**
 * One outstanding contact-linking job, normalised out of the gate status so the screen has a single list to
 * render whether it came from `missing[]` (link this channel) or `reverifyDue[]` (prove this link is live).
 */
export interface ContactTask {
  readonly channel: string;
  readonly purpose: ContactLinkPurposeValue;
  /** The Frist, or null when the task already blocks (or carries no deadline at all). */
  readonly deadline: string | null;
  /** The link being re-verified; null for a first link. Display only — the challenge is keyed on the channel. */
  readonly linkId: string | null;
  /** Whether this job blocks the session right now — see {@link ContactLinkingComponent.plan}. */
  readonly blocks: boolean;
}

/**
 * The contact-linking lockout screen (#558) — the twin of `FormsCompletionComponent`, and deliberately built
 * to the same shape: a screen outside every panel shell that a `restricted` session may reach and nothing
 * else, which does the one thing that lifts its own restriction and then re-issues the session live.
 *
 * ### Two modes, one screen
 *
 * * **Blocking** (`verdict = restricted`). The employee cannot reach the panel until the outstanding channels
 *   are linked. The tasks are worked through one at a time, there is no way out but finishing or signing out,
 *   and finishing re-issues the token and lands them in the panel.
 * * **Voluntary** (`verdict = review`). A requirement inside its Frist, or a re-verification not yet overdue.
 *   The session is fully usable, so the screen says so, names the Frist and offers „Später erledigen" — the
 *   same lesson #404 taught the forms screen, where bouncing a non-blocking session made the whole voluntary
 *   branch unreachable dead code.
 *
 * The guard lets a `review` session stand here on purpose; a session with no contact business at all is
 * bounced by {@link refresh} instead, which has the live gate status the token claim lacks.
 *
 * ### The wizard is not this screen's business
 *
 * Every step of the actual linking — the code, the deep link, the wait, the decline/expiry wording — belongs to
 * {@link ContactLinkWizardComponent}, which renders the server's own guide. This component decides only *which*
 * task is on screen and what happens when one finishes. That split is what lets „Meine Verknüpfungen" offer the
 * identical flow voluntarily without a second copy of it.
 */
@Component({
  selector: 'app-contact-linking',
  standalone: true,
  imports: [DatePipe, ContactLinkWizardComponent],
  templateUrl: './contact-linking.component.html'
})
export class ContactLinkingComponent implements OnInit {
  /** Loading the gate status, or finishing (reissue). */
  readonly loading = signal(true);
  /** A load failure — the screen's only full-stop state, with a retry. */
  readonly errorMessage = signal<string | null>(null);
  /** True while the listed tasks BLOCK the session. False in the voluntary mode. */
  readonly blocking = signal(true);
  /** The outstanding jobs, in the order they are offered. */
  readonly tasks = signal<ContactTask[]>([]);
  /** Which task the wizard is open for, or null (the list / nothing to do). */
  readonly activeIndex = signal<number | null>(null);
  /** The earliest Frist of the listed tasks, for the intro. Null while {@link blocking}. */
  readonly pendingDeadline = signal<string | null>(null);
  /**
   * Restricted, but the server listed nothing to do.
   *
   * Not defensive padding: a requirement for a channel whose bot is unconfigured restricts the session while
   * offering no task, and that is precisely the shape that becomes a permanent dead end if the screen just
   * renders an empty list. It says who can fix it instead.
   */
  readonly blockedNoAction = signal(false);

  protected readonly Purpose = ContactLinkPurpose;

  private readonly contacts = inject(ContactLinkHttpService);
  private readonly auth = inject(AuthenticationService);
  private readonly router = inject(Router);

  /** The channel catalogue from the last status read — display names only. */
  private status: ContactGateStatus | null = null;

  ngOnInit(): void {
    this.refresh();
  }

  /** The task the wizard is showing, or null. */
  get currentTask(): ContactTask | null {
    const index = this.activeIndex();
    return index === null ? null : this.tasks()[index] ?? null;
  }

  get total(): number {
    return this.tasks().length;
  }

  /** The human name of a channel, from the server's catalogue. */
  label(channel: string): string {
    return channelDisplayName(channel, this.status?.channels ?? []);
  }

  /**
   * Why this restricted session has nothing to do — the sentence under „Einrichtung ausstehend".
   *
   * Read off the **channel catalogue** rather than off an error, because in this state no call has failed:
   * the status came back cleanly, said `restricted`, and listed no task. The reason for that is always a
   * channel that cannot currently be started, and the catalogue is where the server says so. Naming
   * `botNotConfigured` specifically matters here more than anywhere else in the feature — this is the one
   * screen with no self-service action at all, so a sentence that sends the reader to a manager who cannot
   * help is the difference between a wait and a wild goose chase.
   *
   * Falls back to the generic wording when no channel explains itself, which is also what an older server or
   * an unrecognised reason produces.
   */
  get unavailableMessage(): string {
    const blocked = (this.status?.channels ?? []).find(c => !c.available);
    return blocked
      ? channelUnavailableMessage(blocked.displayName || this.label(blocked.channel), blocked.unavailableReason)
      : 'Für Ihr Konto ist ein Kontaktkanal erforderlich, der derzeit nicht eingerichtet werden kann. '
        + 'Bitte wenden Sie sich an Ihre Vorgesetzten.';
  }

  /** Re-read the gate status and rebuild the task list. */
  refresh(): void {
    this.loading.set(true);
    this.errorMessage.set(null);
    this.blockedNoAction.set(false);
    this.activeIndex.set(null);

    this.contacts.status().subscribe({
      next: status => {
        this.status = status;
        this.blocking.set(status.verdict === ContactGate.Restricted);

        const tasks = this.plan(status);
        this.tasks.set(tasks);

        // Nothing blocking and nothing to offer ⇒ the panel is where the employee belongs. (A `review` session
        // may legitimately stand here — the guard lets it through — so this is the bounce, with the live gate
        // status the token claim lacks.)
        if (tasks.length === 0 && !this.blocking()) {
          this.leaveToPanel();
          return;
        }
        if (tasks.length === 0) {
          this.blockedNoAction.set(true);
          this.loading.set(false);
          return;
        }

        this.pendingDeadline.set(this.blocking() ? null : earliest(tasks.map(t => t.deadline)));
        // Blocking ⇒ open the first task straight away: there is nothing else to do here, and a list of one
        // item with a button on it is a click that asks nothing.
        this.activeIndex.set(this.blocking() ? 0 : null);
        this.loading.set(false);
      },
      error: () => this.fail('Der Status Ihrer Verknüpfungen konnte nicht geladen werden.')
    });
  }

  /**
   * Turn a gate status into the jobs this screen offers.
   *
   * **Restricted ⇒ only what blocks.** A requirement still inside its grace does not lengthen a lockout
   * wizard; the panel is reachable again the moment the blocking ones are done, and the rest is offered later
   * from „Meine Verknüpfungen".
   *
   * "Blocks" is the **server's** `overdue` wherever it is present, and only falls back to comparing `deadline`
   * with the local clock when the field is absent. The fallback is not equivalent and never was: the grace
   * window is evaluated against the server's clock and the requirement's own policy, so a browser with a
   * skewed clock, or a task with a policy that blocks without carrying a date, is decided wrongly by a date
   * comparison. It is kept for exactly one case — a server that predates the field — and should not be relied
   * on for anything else.
   *
   * The safety valve stays either way: if the filter ever leaves a *restricted* session with an empty list,
   * the whole list is offered. A restricted employee looking at nothing is the one outcome that cannot be
   * allowed, and offering a task early is harmless.
   */
  private plan(status: ContactGateStatus): ContactTask[] {
    const all: ContactTask[] = [
      ...status.missing.map(m => ({
        channel: m.channel, purpose: ContactLinkPurpose.Link, deadline: m.deadline ?? null, linkId: null,
        blocks: blocksNow(m.overdue, m.deadline ?? null)
      })),
      ...status.reverifyDue.map(r => ({
        channel: r.channel, purpose: ContactLinkPurpose.Reverify, deadline: r.deadline ?? null,
        linkId: r.linkId ?? null, blocks: blocksNow(r.overdue, r.deadline ?? null)
      }))
    ];

    if (status.verdict !== ContactGate.Restricted) return all;

    const blocking = all.filter(t => t.blocks);
    return blocking.length > 0 ? blocking : all;
  }

  /** Open the wizard for one task (the voluntary mode's list action). */
  open(index: number): void {
    this.activeIndex.set(index);
  }

  /** Close the wizard without finishing — voluntary mode only. */
  cancel(): void {
    if (this.blocking()) return;
    this.activeIndex.set(null);
  }

  /** Leave without finishing — only offered when the tasks do not block. */
  postpone(): void {
    if (this.blocking()) return;
    this.leaveToPanel();
  }

  /**
   * A task finished. Blocking ⇒ straight on to the next one, or finish; voluntary ⇒ back to a re-read list,
   * because completing one task can change what the others are (a share consent satisfies several employers).
   */
  onTaskCompleted(): void {
    const index = this.activeIndex();
    if (!this.blocking()) {
      this.refresh();
      return;
    }
    if (index !== null && index < this.tasks().length - 1) {
      this.activeIndex.set(index + 1);
      return;
    }
    this.finalize();
  }

  /** All tasks dealt with → re-issue the token and re-evaluate the gate. */
  private finalize(): void {
    this.loading.set(true);
    this.activeIndex.set(null);

    this.contacts.reissue().subscribe({
      next: token => {
        this.auth.applyReissuedSession(token);
        // Still restricted (e.g. a second requirement surfaced, or a share is awaiting another employer's
        // consent) → reload the remaining set rather than dropping the employee into a panel the guard will
        // bounce them straight out of.
        if (this.auth.isContactRestricted) this.refresh();
        else this.leaveToPanel();
      },
      error: () => this.fail('Die Sitzung konnte nicht aktualisiert werden. Bitte versuchen Sie es erneut.')
    });
  }

  logout(): void {
    this.auth.logout();
  }

  private leaveToPanel(): void {
    this.router.navigate(['/' + AppRoutes.EmployeePanel]).then();
  }

  private fail(message: string): void {
    this.loading.set(false);
    this.errorMessage.set(message);
  }
}

/**
 * Whether a task blocks right now: the server's verdict when it gave one, else the local-clock fallback.
 *
 * The fallback treats an absent or already-past deadline as blocking. Deliberately generous in that direction:
 * over-offering a task costs an employee one extra screen, while under-offering one on a restricted session
 * costs them the whole app.
 */
function blocksNow(overdue: boolean | undefined, deadline: string | null): boolean {
  if (typeof overdue === 'boolean') return overdue;
  if (!deadline) return true;
  const time = new Date(deadline).getTime();
  return !Number.isFinite(time) || time <= Date.now();
}

/** The earliest of a set of ISO deadlines, or null when there are none (missing/invalid dates are ignored). */
function earliest(deadlines: (string | null)[]): string | null {
  const times = deadlines
    .filter((d): d is string => !!d)
    .map(d => new Date(d).getTime())
    .filter(t => Number.isFinite(t));
  return times.length ? new Date(Math.min(...times)).toISOString() : null;
}
